import React, { useCallback, useState } from 'react';
import { styled } from '@compiled/react';
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import OpTrigger from '@atlassian/jira-common-util-op-triggers/src/on-mount';
import { AsyncAlertListWidget } from '@atlassian/jira-eoc-alert-list-widget';
import {
	useChangeboardingBroadcastWidget,
	AsyncChangeboardingWidget,
} from '@atlassian/jira-eoc-changeboarding';
import { useOgTenantInfo } from '@atlassian/jira-eoc-tenant-info';
import { AsyncWhoIsOnCallWidget } from '@atlassian/jira-eoc-who-is-on-call-widget';
import { useExperienceSuccess } from '@atlassian/jira-experience-tracker';
import { ff } from '@atlassian/jira-feature-flagging';
import { BroadcastWidget } from '@atlassian/jira-home-broadcast-widget';
import type { main_rightSidePanel_RootQuery } from '@atlassian/jira-relay/src/__generated__/main_rightSidePanel_RootQuery.graphql';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller';
import { NbaExperimentCards } from '@atlassian/jira-your-work-nba-experiment';

import { RIGHT_SIDE_PANEL_EXPERIENCE, RIGHT_PANEL_WIDTH } from '../constants';
import { JiraHomeSidePanelSkipLink } from './skip-link';

const isBroadcastWidgetEnabled = () => false;

interface RightSidePanelProps {
	queryReference: PreloadedQuery<main_rightSidePanel_RootQuery>;
}

export const RightSidePanel = ({ queryReference }: RightSidePanelProps) => {
	const { shouldUseJSM } = useOgTenantInfo();
	const { hasServiceDeskAccess } = useApplicationPermissions();

	/* eslint-disable jira/relay/must-colocate-fragment-spreads */
	const rootQueryRef = usePreloadedQuery<main_rightSidePanel_RootQuery>(
		graphql`
			query main_rightSidePanel_RootQuery($cloudId: ID!, $broadcastWidgetEnabled: Boolean!) {
				...main_homeBroadcastWidget_Widget @include(if: $broadcastWidgetEnabled)
			}
		`,
		queryReference,
	);
	const onSuccess = useExperienceSuccess(RIGHT_SIDE_PANEL_EXPERIENCE);

	const [showSkipLink, setShowSkipLink] = useState(false);
	const containerRef = useCallback((node: HTMLDivElement | null) => {
		if (!node) {
			setShowSkipLink(false);
			return;
		}

		if (typeof ResizeObserver !== 'undefined') {
			const resizeObserver = new ResizeObserver((entries) => {
				const [{ contentRect }] = entries;
				setShowSkipLink(contentRect.width > 0);
			});
			resizeObserver.observe(node);
		}
	}, []);
	const changeboardingWidget = ff('opsgenie-consolidation-transitioning-state_d7vm9')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useChangeboardingBroadcastWidget()
		: undefined;

	return (
		<Container id="jira-home-side-panel" ref={containerRef}>
			<NbaExperimentCards />
			{showSkipLink && <JiraHomeSidePanelSkipLink />}
			{isBroadcastWidgetEnabled() && <BroadcastWidget queryReference={rootQueryRef} />}
			{changeboardingWidget?.isEnabled && <AsyncChangeboardingWidget />}
			{hasServiceDeskAccess && ff('eoc-who-is-on-call-widget_dtqxx') && shouldUseJSM && (
				<AsyncWhoIsOnCallWidget />
			)}
			{hasServiceDeskAccess && ff('alerts-list-widget-on-your-work_kn81n') && shouldUseJSM && (
				<AsyncAlertListWidget />
			)}
			<OpTrigger op={onSuccess} />
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.aside({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${RIGHT_PANEL_WIDTH}px`,
	position: 'sticky',
	height: 'auto',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface.sunken', colors.N10),
	zIndex: 1,
	overflowY: 'auto',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	padding: `${token('space.300', '24px')} ${token('space.200', '16px')} ${token(
		'space.400',
		'32px',
	)}`,
	gap: token('space.200', '16px'),
	boxSizing: 'border-box',
	'&:empty': {
		display: 'none',
	},
});
